import { Box, Card, Grid, Switch, Typography } from '@material-ui/core'
import MUIButton from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import Tooltip from '@material-ui/core/Tooltip'
import InfoIcon from '@material-ui/icons/Info'
import { makeStyles } from '@material-ui/styles'
import get from 'lodash/get'
import { cloneElement, useEffect, useState } from 'react'
import {
  ArrayField,
  Datagrid,
  DateField,
  FunctionField,
  NumberField,
  Tab,
  TabbedShowLayout,
  TextField,
  useNotify,
  usePermissions,
  useRefresh,
  useShowController,
} from 'react-admin'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Button } from 'shared/components/Button/Button'
import { SimpleDialog } from 'shared/components/Dialog/SimpleDialog'
import { Notes } from 'shared/components/Notes/Notes'
import { usePageTitle } from 'shared/components/PageTitle/PageTitle'
import { Text } from 'shared/components/Text/Text'
import { CamelToSnakeCaseNested } from 'shared/helpers/typeHelper'
import { useRepaymentsBreakdowns } from 'shared/hooks/query/useRepaymentsBreakdowns'
import { RepaymentRiskTier, RiskTierText, RiskTierTooltipText } from 'shared/models/repayments'
import { ActiveDriverStatuses, TeamMembership } from 'shared/models/user'
import { bgGray, darkGray, midGray, primaryBlack, royalBlue } from 'shared/theme/colors'
import styled from 'styled-components'

import { AddressEditDialog } from 'components/Common/AddressForm'
import { clearTeamBillingAddress, updateTeamBillingAddress, updateToggleableTeamPerms } from '../../api/core'
import { stripebaseUrl } from '../../config/config'
import { EquipmentType, FSM_TRANSITION_RECORD_TYPE, TeamCompositionMap } from '../../helpers/constants'
import { getProductNameFromRecord } from '../../helpers/utils'
import { ProductSubscription, ProductSubscriptionBundleName } from '../../models/subscription'
import { Team } from '../../models/team'
import { hasPermission, Permissions } from '../../Permissions'
import Aside from '../Aside/aside'
import { BaseActionDetailButton } from '../Common/ActionButtons'
import { consoleTooltipStyles } from '../Common/Tooltip'
import { ConsoleUserProvider } from '../Context/ConsoleUserProvider'
import { CopyToClipboardButton } from '../CopyToClipboardButton/CopyToClipboardButton'
import { EquipmentQuickCreateUpdateButton } from '../Equipment/EquipmentQuickCreateUpdateButton'
import EquipmentSection from '../Equipment/EquipmentSection'
import { CustomBooleanField, JobsReferenceManyList, LabeledTypographyField } from '../Fields'
import { FSMButtonToolbar } from '../FSM/Buttons/FSMTransitionButton'
import { PolicyDetails } from '../Policies/PolicyDetails'
import { UserLink } from '../User/UserLink'
import TeamConnectedIntegrations from './TeamConnectedIntegrations'
import { TeamCredit } from './TeamCredit'
import TeamDocuments from './TeamDocuments'
import { EldAccountLabel, ELDTab } from './TeamELDAccount'
import { TeamEntitlements } from './TeamEntitlements'
import { TeamMembers } from './TeamMembers'
import TeamMonthlyStatementsExportButton from './TeamMonthlyStatementsExportButton'
import TeamNOAAcknowledgments from './TeamNOAAcknowledgments'
import { TeamRepayments } from './TeamRepayments'
import { TeamSafetyDashboard } from './TeamSafetyDashboard'
import { TeamStripeSubscription } from './TeamStripeSubscription'
import TeamTaxId from './TeamTaxId'

const TeamStatusToolbar = ({ record, permissions, children }: any) => (
  <FSMButtonToolbar
    classes={null}
    record={record}
    recordType={FSM_TRANSITION_RECORD_TYPE.team}
    isHidden={!hasPermission(permissions, Permissions.ChangeTeam)}
  >
    {children}
  </FSMButtonToolbar>
)

const TogglePermissionsButton = ({
  canChangeTeamPermission,
  handleEditPermissionsButtonClick,
}: {
  canChangeTeamPermission: boolean
  handleEditPermissionsButtonClick: () => void
}) => {
  if (canChangeTeamPermission) {
    return (
      <Button variant="text" onClick={handleEditPermissionsButtonClick}>
        Edit Driver Permissions
      </Button>
    )
  }
  return null
}

const EditPermissionsBar = ({
  record,
  permsModalOpen,
  setPermsModalOpen,
  toggleablePermissions,
  setToggleablePermissions,
  handleTogglePermissionsUpdate,
}: {
  record: Record<string, any>
  permsModalOpen: boolean
  setPermsModalOpen: (e: boolean) => void
  toggleablePermissions: any
  setToggleablePermissions: (e: any) => void
  handleTogglePermissionsUpdate: (record: any, toggleable_permissions: any) => void
}) => {
  const setTogglePerm = (checked: boolean, name: string) => {
    setToggleablePermissions({
      ...toggleablePermissions,
      [name]: checked,
    })
  }
  if (!record || !record?.toggleable_permissions) return null

  const permToSentenceMap: Record<string, string> = {
    team_user_can_book_loads: 'View load board and book loads',
    team_user_can_view_rates: 'View load prices for assigned jobs',
  }

  return (
    <Dialog open={permsModalOpen} onClose={() => setPermsModalOpen(false)}>
      <DialogContent>
        <Typography variant="h5"> Edit Team Permissions </Typography>

        <ArrayField
          record={{
            ...record,
            id: record.id,
            toggleable_permissions: Object.keys(record.toggleable_permissions).map((perm) => ({
              name: perm,
              label: permToSentenceMap[perm],
              enabled: record.toggleable_permissions[perm].enabled,
            })),
          }}
          source="toggleable_permissions"
        >
          <Datagrid optimized>
            <TextField source="label" />

            <FunctionField
              label="Enabled"
              render={(record: any) => (
                <Switch
                  checked={toggleablePermissions[record.name]}
                  value={record.enabled}
                  onChange={(_, checked) => setTogglePerm(checked, record.name)}
                />
              )}
            />
          </Datagrid>
        </ArrayField>
      </DialogContent>
      <DialogActions>
        <Button
          variant="primary"
          onClick={() => handleTogglePermissionsUpdate(record, toggleablePermissions)}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const CustomTeamShow = (props: any) => {
  const {
    basePath, // deduced from the location, useful for action buttons
    loaded, // boolean that is false until the record is available
    record, // record fetched via dataProvider.getOne() based on the id from the location
    resource, // the resource name, deduced from the location. e.g. 'posts'
    version, // integer used by the refresh feature
  } = useShowController(props)
  const canChangeTeamProduct = hasPermission(props.permissions || [], Permissions.CanChangeTeamProduct)
  const tooltipClasses = consoleTooltipStyles()
  const [showSafetyDashboard, setShowSafetyDashboard] = useState(false)
  const { permissions } = usePermissions()

  if (!loaded || !record) return null
  const drivers =
    record?.members?.filter(
      (member: CamelToSnakeCaseNested<TeamMembership>) =>
        member.is_team_driver && ActiveDriverStatuses.includes(member.driver_status)
    ).length || 0
  record.repaymentsBreakdowns = props.repaymentsBreakdowns

  return (
    <div>
      <ConsoleUserProvider
        isPremium={false}
        isFlexStarter={false}
        hasStarterFeature={false}
        hasFactoringFeature={false}
        permissions={permissions}
      >
        <Notes
          recordId={record.id as string}
          recordType="console/teams"
          recordName={record.name}
          expandByDefault={false}
          apiVersion="v1"
        >
          {(renderOpenNotesCard) => (
            <div>
              <FlexBox mt={4}>
                <Grid
                  container
                  direction="column"
                  spacing={3}
                  justifyContent="space-between"
                  style={{ overflow: 'scroll' }}
                >
                  <Grid item>
                    <FlexBox gap="50px">
                      <div>
                        <FlexBox alignItems="center" gap="20px">
                          <Text variant="header2" mt={-1}>
                            {record.name}
                          </Text>
                          <Text variant="paragraph1" mt={0.5}>
                            <FlexBox alignItems="center" gap="20px">
                              <span>
                                {drivers} driver{drivers > 1 ? 's' : ''}
                              </span>
                              <TeamMembers
                                teamId={record.id as string}
                                titleRender={(total: number) => (
                                  <Text variant="paragraph1">{total} total</Text>
                                )}
                              />
                            </FlexBox>
                          </Text>
                        </FlexBox>

                        <Box mt={1.5} />
                        <TeamEntitlements entitlements={record.entitlements} team={record as Team} />
                        <Box mt={2} />
                        <div style={{ display: 'inline-block' }}>
                          <FlexBox gap="10px" mt={1}>
                            {canChangeTeamProduct && (
                              <Button variant="text" href={`${basePath}/${record.id}/request-bundle-change`}>
                                Change Plan
                              </Button>
                            )}
                          </FlexBox>
                          <FlexBox mt={2}>
                            <Text variant="header4" fontColor={primaryBlack}>
                              {record.product_subscription?.has_dispatch_service_feature &&
                                'Dispatch Service'}
                            </Text>
                            <Text variant="header4" fontColor={primaryBlack}>
                              {record.product_subscription?.has_dispatch_service_free_feature &&
                                'Dispatch Service Free'}
                            </Text>
                            <Text
                              variant="label4"
                              fontColor={midGray}
                              style={{ padding: '7px', paddingRight: '7px', paddingTop: '2px' }}
                            >
                              {record.product_subscription?.has_dispatch_service_feature && '|'}
                            </Text>
                            <Text variant="header4" fontColor={primaryBlack}>
                              {record.product_subscription?.has_truck_lease_feature && 'Truck Lease'}
                            </Text>
                          </FlexBox>
                        </div>
                      </div>

                      <FlexBox isColumn>
                        <Text variant="uppercaseLabel2" fontColor={midGray}>
                          FLEET LIABILITY
                        </Text>
                        <FlexBox alignItems="center" gap="10px">
                          <Text variant="label2" fontColor={midGray}>
                            {RiskTierText[record.repaymentsBreakdowns?.risk_tier || RepaymentRiskTier.LOW]}
                          </Text>
                          <Tooltip
                            placement="right"
                            classes={{ tooltip: tooltipClasses.tooltip }}
                            title={
                              <Text variant="paragraph2" fontColor={darkGray}>
                                {
                                  RiskTierTooltipText[
                                    record.repaymentsBreakdowns?.risk_tier || RepaymentRiskTier.LOW
                                  ]
                                }
                              </Text>
                            }
                          >
                            <InfoIcon className={tooltipClasses.infoIcon} color="primary" fontSize="small" />
                          </Tooltip>
                        </FlexBox>
                      </FlexBox>

                      <div>{renderOpenNotesCard()}</div>
                    </FlexBox>
                  </Grid>
                  <Grid item>
                    <Card style={{ marginTop: '20px' }}>
                      {cloneElement(props.children, {
                        basePath,
                        record,
                        resource,
                        version,
                      })}
                    </Card>
                  </Grid>
                  <Grid item>
                    <TeamStatusToolbar record={record} {...props}>
                      <Grid item>
                        <TeamMonthlyStatementsExportButton teamId={record?.id} />
                      </Grid>

                      {!record.is_premium && (
                        <Grid item>
                          <MUIButton
                            onClick={() => setShowSafetyDashboard(true)}
                            variant="contained"
                            disabled={!record.dot_number}
                            color="primary"
                          >
                            View Safety dashboard
                          </MUIButton>
                        </Grid>
                      )}
                    </TeamStatusToolbar>
                    <Aside record={record} resource="teams" />

                    {showSafetyDashboard && (
                      <SimpleDialog
                        isOpen
                        onClose={() => setShowSafetyDashboard(false)}
                        maxWidth="900px"
                        backgroundColor={bgGray}
                      >
                        <TeamSafetyDashboard team={record as Team} />
                      </SimpleDialog>
                    )}
                  </Grid>
                </Grid>
              </FlexBox>
            </div>
          )}
        </Notes>
      </ConsoleUserProvider>
    </div>
  )
}

const BillingAddressButtonSection = ({ record, permissions }: any) => {
  if (!record || !permissions) {
    return null
  }
  const canEditTeam = hasPermission(permissions, Permissions.ChangeTeam)
  const addressExists = get(record, 'billing_address')

  return (
    canEditTeam && (
      <div style={{ display: 'flex' }}>
        <AddressEditDialog
          submit={updateTeamBillingAddress}
          isBilling={false}
          record={record}
          address_name="billing_address"
        />
        {addressExists && (
          <BaseActionDetailButton
            buttonStyle={{ marginLeft: '20px' }}
            variant="text"
            record={record}
            actionRequest={clearTeamBillingAddress}
            actionTitle="Remove Billing Address"
            actionText="This team's billing address will be deleted. Are you sure?"
          />
        )}
      </div>
    )
  )
}

const TeamCompositionField = ({ record }: any) => {
  if (!record) {
    return null
  }
  let teamComposition = get(record, 'composition', 'N/A')
  let detailString = get(TeamCompositionMap, teamComposition, 'N/A')
  return (
    <>
      <LabeledTypographyField
        label="Team Composition (Abbreviated)"
        variant="body1"
        value={teamComposition}
      />
      <LabeledTypographyField label="Team Composition (Expanded)" variant="body2" value={detailString} />
    </>
  )
}

const useTeamStyles = makeStyles(() => ({
  addEquipmentLayout: {
    display: 'flex',
    flexDirection: 'row',
  },
}))

const TeamShow = (props: any) => {
  const [permsModalOpen, setPermsModalOpen] = useState(false)
  const { permissions } = usePermissions()
  const canAddEquipment = permissions && hasPermission(permissions, Permissions.AddEquipment)
  const classes = useTeamStyles()
  const [toggleablePermissions, setToggleablePermissions] = useState<Record<string, boolean>>({})
  const { record }: any = useShowController(props)
  const canChangeTeamPermission = hasPermission(props.permissions || [], Permissions.ChangeTeamPermission)

  const productSubscription = record?.product_subscription?.product_name

  if (record) record.product_name = getProductNameFromRecord(record, true)

  const { repaymentsBreakdowns } = useRepaymentsBreakdowns({ teamId: record?.id })

  const notify = useNotify()
  const refresh = useRefresh()

  const handleTogglePermissionsUpdate = async (record: any, toggleable_permissions: any) => {
    const permission_names = Object.keys(toggleable_permissions).map((perm) => ({
      name: perm,
      enabled: toggleable_permissions[perm],
      group: 'DRIVER',
    }))

    updateToggleableTeamPerms(record.id, { permission_names: permission_names })
      .then(() => {
        setPermsModalOpen(false)
        notify('Success!', { type: 'success' })
        refresh()
      })
      .catch((error: any) => {
        setPermsModalOpen(false)
        notify(JSON.stringify(error.response.data), { type: 'error' })
      })
  }

  const rolesColumn = (roles: any) =>
    [
      roles.includes('TEAM_ADMIN') && 'Admin',
      roles.includes('DRIVER') && 'Driver',
      roles.includes('BUSINESS_OWNER') && 'Business Owner',
    ]
      .filter(Boolean)
      .join(' • ')

  useEffect(() => {
    if (repaymentsBreakdowns && record && !record?.repaymentsBreakdowns) {
      record.repaymentsBreakdowns = repaymentsBreakdowns
    }
  }, [record, repaymentsBreakdowns])

  useEffect(() => {
    if (record && record.toggleable_permissions) {
      const toggleable_permissions = Object.fromEntries(
        Object.entries(record.toggleable_permissions).map(([k, v]: [string, any]) => [k, v.enabled])
      )
      setToggleablePermissions(toggleable_permissions)
    }
  }, [record])

  usePageTitle(record?.name)

  return (
    <CustomTeamShow
      {...props}
      classes={classes}
      repaymentsBreakdowns={repaymentsBreakdowns}
      toggleablePermissions={toggleablePermissions}
      setToggleablePermissions={setToggleablePermissions}
    >
      <TabbedShowLayout>
        <Tab label="Overview">
          <FlexBox alignItems="flex-end" gap="20px">
            <LabeledTypographyField label="Id" variant="body2" value={props.id} />
            <CopyToClipboardButton text={props.id} styles="margin-bottom: 7px;" />
          </FlexBox>

          <TeamCompositionField />
          <DateField source="date_created" options={{ timeZoneName: 'short' }} />
          <FunctionField
            label="Location"
            render={(record: any) => (
              <>
                {record.location_city}, {record.location_state}
              </>
            )}
          />
          <TextField source="product_name" label="Product Subscription" />
          {record?.offboarding_status === 'EXECUTING' && (
            <TextField source="escrow_balance" label="Escrow Balance" />
          )}
          <TextField source="dot_number" />
          <TextField source="mc_number" />
          <TextField source="legal_formation_state" />
          <TextField source="business_type" />
          <CustomBooleanField source="is_approved" />
          <FunctionField
            label="Status"
            render={(record: any) =>
              record.offboarding_status === 'EXECUTING' ? 'OFFBOARDING_IN_PROGRESS' : record.status
            }
          />
          <TextField source="status_detail" />
          <TextField source="status_reason" />
          <CustomBooleanField source="rates_visible_to_non_admins" />
          <TextField source="referral_source" />
          <FunctionField
            label="Stripe account id"
            render={(record: any) => (
              <Link
                href={`${stripebaseUrl}connect/accounts/${record.stripe_account_id}/activity`}
                target="_blank"
                rel="noreferrer"
              >
                {record.stripe_account_id}
              </Link>
            )}
          />
          <FunctionField
            label="Stripe customer id"
            render={(record: any) => (
              <Link
                href={`${stripebaseUrl}customers/${record.stripe_customer_id}`}
                target="_blank"
                rel="noreferrer"
              >
                {record.stripe_customer_id}
              </Link>
            )}
          />
          <FunctionField
            label="Stripe subscription account customer id"
            render={(record: any) => (
              <Link
                href={`${stripebaseUrl}customers/${record.stripe_subscription_account_customer_id}`}
                target="_blank"
                rel="noreferrer"
              >
                {record.stripe_subscription_account_customer_id}
              </Link>
            )}
          />
          <NumberField source="num_trailers_dry_van" />
          <NumberField source="num_trailers_flatbed" />
          <NumberField source="num_trailers_reefer" />
          <TeamTaxId permissions={permissions} />
          <br />
        </Tab>
        <Tab label="Documents">
          <TeamDocuments permissions={permissions} />
        </Tab>
        <Tab label="Billing Address" path="billing-address">
          <TextField source="billing_address.line1" label="Line 1" />
          <TextField source="billing_address.line2" label="Line 2" />
          <TextField source="billing_address.city" label="City" />
          <TextField source="billing_address.state" label="State" />
          <TextField source="billing_address.zip" label="Zip" />
          <BillingAddressButtonSection permissions={permissions} />
        </Tab>
        <Tab label="Equipment" path="equipment">
          {canAddEquipment && (
            <div>
              <EquipmentQuickCreateUpdateButton
                record={{ id: props.id }}
                equipmentType={EquipmentType.Truck}
                isCreate
              />
              <EquipmentQuickCreateUpdateButton
                record={{ id: props.id }}
                equipmentType={EquipmentType.Trailer}
                isCreate
              />
            </div>
          )}
          <EquipmentSection permissions={permissions} equipmentType={EquipmentType.Truck} />
          <EquipmentSection permissions={permissions} equipmentType={EquipmentType.Trailer} />
        </Tab>
        <Tab label="Members" path="members">
          <EditPermissionsBar
            record={record}
            setPermsModalOpen={setPermsModalOpen}
            permsModalOpen={permsModalOpen}
            toggleablePermissions={toggleablePermissions}
            setToggleablePermissions={setToggleablePermissions}
            handleTogglePermissionsUpdate={handleTogglePermissionsUpdate}
          />
          <Box mt={2} />
          <TogglePermissionsButton
            canChangeTeamPermission={canChangeTeamPermission}
            handleEditPermissionsButtonClick={() => {
              setPermsModalOpen(true)
            }}
          />
          <Box mt={2} />
          <ArrayField source="members">
            <Datagrid>
              <FunctionField render={(record: any) => <UserLink user={record} />} />
              <TextField source="email" />
              <TextField source="mobile" />
              <FunctionField label="roles" render={(record: any) => rolesColumn(record.roles)} />
              <CustomBooleanField source="is_team_driver" />
              <CustomBooleanField source="is_team_admin" />
              <TextField source="driver_status_human" label="Driver Status" />
              <EldAccountLabel label="ELD status" />
            </Datagrid>
          </ArrayField>
        </Tab>
        <Tab label="Jobs" path="jobs">
          <JobsReferenceManyList
            target="team_id"
            showUser
            is_premium={record?.bundle_name === ProductSubscriptionBundleName.PREMIUM}
          />
        </Tab>

        {productSubscription === ProductSubscription.FLEX && (
          <Tab label="Broker Setup" path="connected-integrations">
            <TeamConnectedIntegrations permissions={permissions} />
          </Tab>
        )}

        {productSubscription === ProductSubscription.CASH && (
          <Tab label="NOA Acknowledgments" path="noa-acknowledgments">
            <TeamNOAAcknowledgments />
          </Tab>
        )}

        <Tab label="ELD Account" path="eld-setup">
          <ELDTab />
        </Tab>
        <Tab label="Credit" path="credit">
          <TeamCredit permissions={permissions} />
        </Tab>
        {record?.bundle_name !== ProductSubscriptionBundleName.PREMIUM && (
          <Tab label="Subscription" path="subscription">
            <TeamStripeSubscription permissions={permissions} />
          </Tab>
        )}
        <Tab label="Deductions" path="repayments">
          <TeamRepayments permissions={permissions} show={PolicyDetails} />
        </Tab>
      </TabbedShowLayout>
    </CustomTeamShow>
  )
}

const Link = styled.a`
  text-decoration: none;
  color: ${royalBlue};
`

export default TeamShow
